import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import "../JaldiLab.css";
import {
  DashedBorderStyle,
  getTextContentHeading,
  getTextContentWithHeading,
  h1style,
  h2style,
  h3style,
  paraStyle,
  referenceHtml,
  referenceTootKit,
  sectionStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import medicalRoom1 from "../../../assets/Images/medical_room1.jpeg";
import medicalRoom3 from "../../../assets/Images/medical_room3.jpeg";
import medicalRoom4 from "../../../assets/Images/MedicalRoom Images/medical_room4.jpeg";
import medicalRoom5 from "../../../assets/Images/MedicalRoom Images/medical_room5.jpeg";
import defibrillator from "../../../assets/Images/Defibrillator.jpeg";
import parkingAmbulance from "../../../assets/Images/parking_for_ambulance.jpeg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import { ImageDesc88, ImageDesc89, ImageDesc90 } from "../../../utils/tmp/ImageDescription/MedicalServiceID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

function MedicalServices({ goToSection }) {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: "Harmonised Guidelines 2021, p. 342, Section 6.5. ",
      link: "",
      description: "",
    },
  ];

  const ObjectivesArea = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Plan for amenities that will{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          address the needs of diverse users
        </span>{" "}
        and provide a comfortable and efficient environment for all user groups;
      </>,
      <>
        Ensure that
        <span style={{ color: "#DC002D" }}>
          {" "}
          medical rooms are well-equipped with the necessary infrastructure and
          medical equipment
        </span>{" "}
        to handle emergencies, basic healthcare needs, and provide a suitable
        environment for medical examinations;
      </>,

      <>
        Ensure that
        <span style={{ color: "#DC002D" }}>
          {" "}
          prompt and immediate medical assistance
        </span>{" "}
        is provided to individuals who may require
        <span style={{ color: "#DC002D" }}>
          {" "}
          emergency care or first aid
        </span>{" "}
        within the Court Complex;
      </>,

      <>
        <span style={{ color: "#DC002D" }}>
          Establish partnerships with local healthcare providers or nearby
          hospitals{" "}
        </span>
        to ensure that individuals in need of medical attention can be quickly
        transported to a medical facility;
      </>,

      <>
        In instances where the Court Complex may not have a hospital in the
        close proximity,
        <span style={{ color: "#DC002D" }}>
          {" "}
          provide dedicated medical service with trained personnel
        </span>{" "}
        to ensure efficient healthcare service.
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        A designated parking area for the ambulance attached with the Medical
        Centre must be provided in close proximity to the Centre. For further
        details on parking, please refer to{" "}
        <span
          onClick={() => goToSection("spacePlanningCirculation", "parking")}
          style={sectionStyle}
        >
          Section 1.3.
        </span>
      </>,
      "A direct accessible path must be provided to ensure that patients on stretches may be taken directly to the ambulance on short notice.",
      "The Medical Centre must have direct access to or be in close proximity to sanitary provisions such as an accessible washroom Complex, drinking water, etc.",
    ],
    isOrdered: true,
    isImage: true,
  };

  const DesignConsiderations = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        "The medical services provided at the Medical Centre must be accessible to diverse user groups. ",

        "The Centre must be well-ventilated and must have adequate natural light. ",

        "The Centre must include provisions for a lobby area, rooms for doctors, a nurses’ station, and an examination room. ",
      ],
    },
    {
      heading: "Lobby Area",
      items: [
        "The Medical Centre must have its own lobby area with a reception counter. ",

        "The lobby area must be equipped with adequate ergonomic seating for people waiting their turn to visit the facilities. ",

        <>
          It must also have a play area for children. For details of the design
          of a play area, please refer to{" "}
          <span
            onClick={() =>
              goToSection("familyChildFriendlyServices", "playArea")
            }
            style={sectionStyle}
          >
            Section 3.3.C
          </span>
          .
        </>,

        "The reception at the lobby area must have information flyers/personnel informing the visitors regarding the facilities being provided  at the centre.",

        <>
          A Public Address (“PA”) system must be provided in case of emergencies
          like fires, natural disasters, etc., to assist in evacuations,
          supported by uninterrupted power supply.
          {reference(referenceData[0], 1)}
        </>,
      ],
    },
    {
      heading: "Medical Rooms",
      items: [
        "An adequate number of medical rooms must be provided, depending on the number of visitors to the Court Complex. ",

        "The medical rooms must have adequate space for medical equipment to be accommodated, including stocks of emergency medications, equipment for Echocardiograms (“ECGs”), non-invasive blood pressure, pulse oximetry for oxygen saturation, and monitors for checking basic vital signs.",

        "Automated External Defibrillators (“AED”) must be provided in the medical rooms, with medical staff being trained to use them.",

        "The medical room must be equipped with an adequate number of beds with screening curtains, to ensure privacy of the persons using the services. ",

        "Adequate storage facilities must be provided for portable medical equipment.",

        "The medical room must be provided with a sink and tap with thermostatic mixing valves.",

        <>
          Facilities for segregating medical waste including specific bins to
          clear sharp objects must be provided.
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CaptionWithBorders1
              image={[medicalRoom1]}
              zoomImgStyle={{ height: "400px" }}
            />
            <CaptionWithBorders1
              image={[medicalRoom5]}
              zoomImgStyle={{ height: "400px" }}
            />
            <CaptionWithBorders1
              image={[medicalRoom4]}
              zoomImgStyle={{ height: "400px" }}
            />
          </div>
          <CaptionWithBorders1
            image={[medicalRoom3]}
            caption="Image 88: Examples of medical rooms"
            creditLabel="Image Credit"
            imageCredit="369 Ochre Studio"
            zoomImgStyle={{ height: "400px" }}
            imageDescription={ImageDesc88}
          />
          <CaptionWithBorders1
            image={[defibrillator]}
            zoomImgStyle={{ height: "500px" }}
            caption={
              "Image 89: Example of an Automated External Defibrillator (“AED”)"
            }
            creditLabel={"Image Credit"}
            imageCredit={"369 Ochre Studio"}
            imageDescription={ImageDesc89}
          />
        </>,
      ],
    },
    {
      heading: "Space for Doctor-on-call & Staff",
      items: [
        "Adequate office space must be provided for the doctors deputed to the centre, as well as nursing staff.",
        <>
          The office must be equipped with desks with integrated power and data,
          ergonomic chairs, and storage facilities for medical files as well as
          personal belongings of the personnel. For further details of
          workspaces, please refer to{" "}
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesSingleWorkspace")
            }
            style={sectionStyle}
          >
            Section 2.5.B
          </span>{" "}
          and{" "}
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
            }
            style={sectionStyle}
          >
            2.5.C.
          </span>
        </>,
      ],
    },
    {
      heading: "Emergency Transportation",
      items: [
        <>
          A parking area for the ambulance must be designated which is attached
          to the medical centre, clear of any obstruction. For further details
          regarding parking, refer to
          <span
            onClick={() => goToSection("/", "user-centric")}
            style={sectionStyle}
          >
            {" "}
            Section 1.3
          </span>
          .
        </>,

        "An accessible ambulance must be made available 24/7 within the Court Complex. ",

        "The ambulance must be equipped with medical equipment and trained medical staff. ",
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 id="commonAmenities" style={h2style}>
          <span style={{ fontSize: "24px" }}>3. </span>COMMON AMENITIES
        </h2>
        <h3 id="mainheading" style={h3style}>
          <span style={{ fontSize: "20px" }}>3.4. </span>Medical Services
        </h3>
        <p style={paraStyle}>
          The Court Complex must provide healthcare facilities and first aid
          services, to ensure that any medical emergencies or health-related
          issues which may arise at the Court Complex are addressed.
        </p>
        <p style={paraStyle}>
          In the case of any emergency, the medical staff deputed at the Court
          Complex must be equipped to initiate emergency response procedures.
          This may involve local emergency services, such as paramedics or an
          ambulance, to provide immediate medical assistance.
        </p>
        {getTextContentHeading(ObjectivesArea)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(DesignConsiderations)}
        <CaptionWithBorders1
          image={[parkingAmbulance]}
          caption={"Image 90: Example of designated parking for ambulances"}
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc90}
        />
        <h2 ref={referencesRef} id="reference" style={referenceStyle}>
          <i>References</i>
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
}

export default MedicalServices;
